import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from '../../TokenAwareTextField';
import {
  useEnsureJourneyMapsLoaded,
  useJourneyMaps,
  useJourneyMapTypes,
} from '../../../../../../hooks/journey-map-hooks';
import { JourneyMap } from '../../../../../../types/journeyMap';

/**
 * Searchable picker over the Redux-backed Journey Map catalogue. Emits the numeric map id (or null
 * when cleared). The list is populated via `useEnsureJourneyMapsLoaded()` — it's a no-op once the
 * slice is hydrated, so it's safe to mount repeatedly. The map's type (its "kind") is shown as a
 * secondary label when the reference data is available.
 */
export function JourneyMapPicker(props: {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
}): JSX.Element {
  const { value, onChange, disabled } = props;
  useEnsureJourneyMapsLoaded();
  const maps = useJourneyMaps();
  const types = useJourneyMapTypes();

  const typeName = useMemo(() => {
    const byId = new Map(types.map((t) => [t.id, t.name]));
    return (map: JourneyMap): string | null =>
      map.journey_type != null ? byId.get(map.journey_type) ?? null : null;
  }, [types]);

  const options = useMemo(
    () => maps.filter((m): m is JourneyMap & { id: number } => m.id != null),
    [maps]
  );

  const selected = useMemo(
    () => (value != null ? options.find((m) => m.id === value) ?? null : null),
    [options, value]
  );

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_e, next) => onChange(next?.id ?? null)}
      getOptionLabel={(map) => map.name || `Journey map #${map.id}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(liProps, map) => {
        const kind = typeName(map);
        return (
          <Box component="li" {...liProps} key={map.id}>
            <Box>
              <Typography variant="body2">{map.name || `Journey map #${map.id}`}</Typography>
              {(kind || map.status) && (
                <Typography variant="caption" color="text.secondary">
                  {[kind, map.status].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label="Journey Map" placeholder="Search journey maps…" />
      )}
    />
  );
}
